import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, CheckCircle2, Clock, Ticket as TicketIcon, MessageSquare, FolderKanban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CATEGORY, PRIORITY, TICKET_STATUS, initials, timeAgo, fmtDate, AgingBadge, Dot } from "@/lib/ui";
import { useAuth } from "@/context/AuthContext";
import CreateTicketDialog from "@/components/CreateTicketDialog";
import TicketDrawer from "@/components/TicketDrawer";

function Stat({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tint}`}><Icon className="w-5 h-5" /></div>
      <div><p className="text-xl font-display font-bold text-slate-900 leading-none">{value}</p><p className="text-xs text-slate-500 mt-1">{label}</p></div>
    </div>
  );
}

export default function ClientDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);

  const load = async () => {
    const { data } = await api.get("/client/overview");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-8 text-slate-400">Loading your workspace…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1200px]" data-testid="client-dashboard">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">
          {user?.company || "Your"} Workspace
        </h1>
        <p className="text-sm text-slate-500 mt-1">Track the status of your project and raise support tickets.</p>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <Stat icon={FolderKanban} label="Your tasks" value={data.stats.tasks} tint="bg-indigo-50 text-indigo-600" />
        <Stat icon={CheckCircle2} label="Completed" value={data.stats.completed_tasks} tint="bg-emerald-50 text-emerald-600" />
        <Stat icon={TicketIcon} label="Open tickets" value={data.stats.open_tickets} tint="bg-sky-50 text-sky-600" />
      </div>

      <Tabs defaultValue="tasks">
        <TabsList className="mb-4">
          <TabsTrigger value="tasks" data-testid="client-tasks-tab">Project tasks ({data.tasks.length})</TabsTrigger>
          <TabsTrigger value="tickets" data-testid="client-tickets-tab">My tickets ({data.tickets.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="space-y-3">
          {data.tasks.map((t) => (
            <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`client-task-${t.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{t.title}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    <Badge variant="outline" className={`h-5 px-1.5 text-[10px] ${CATEGORY[t.category]?.chip}`}>{t.stage_name}</Badge>
                    <Badge variant="outline" className="h-5 px-1.5 text-[10px] border-slate-200 text-slate-500">{t.board_name}</Badge>
                    <AgingBadge age={t.age_days} inStage={t.days_in_stage} />
                    {t.due_date && <span className="text-[11px] text-slate-400">Due {fmtDate(t.due_date)}</span>}
                  </div>
                </div>
              </div>
              {t.recent_notes?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 flex items-center gap-1"><MessageSquare className="w-3 h-3" />Latest updates</p>
                  {t.recent_notes.slice(0, 2).map((n) => (
                    <div key={n.id} className="flex items-start gap-2">
                      <Avatar className="w-5 h-5 mt-0.5"><AvatarImage src={n.author_avatar} /><AvatarFallback className="bg-slate-100 text-slate-500 text-[8px]">{initials(n.author_name)}</AvatarFallback></Avatar>
                      <p className="text-xs text-slate-600 flex-1"><span className="font-medium text-slate-700">{n.author_name}</span> · {timeAgo(n.created_at)}<br />{n.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {data.tasks.length === 0 && <p className="text-sm text-slate-400 py-10 text-center">No tasks are assigned to your account yet.</p>}
        </TabsContent>

        <TabsContent value="tickets">
          <div className="flex justify-end mb-3">
            <Button onClick={() => setCreating(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="client-raise-ticket-button"><Plus className="w-4 h-4 mr-1.5" />Raise ticket</Button>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden">
            {data.tickets.map((t) => (
              <button key={t.id} onClick={() => setActive(t.id)} data-testid={`client-ticket-${t.id}`}
                className="w-full text-left px-4 py-3.5 hover:bg-slate-50 transition-colors flex items-center gap-4">
                <Dot className={PRIORITY[t.priority]?.dot} />
                <span className="font-mono text-xs font-semibold text-slate-500 w-20 shrink-0">{t.key}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{t.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{t.age_days}d old · {t.comments_count} replies</p>
                </div>
                <Badge variant="outline" className={`h-5 px-1.5 text-[10px] ${TICKET_STATUS[t.status]?.chip}`}>{TICKET_STATUS[t.status]?.label}</Badge>
              </button>
            ))}
            {data.tickets.length === 0 && <div className="py-12 text-center text-slate-400">No tickets yet. Raise one if you need help.</div>}
          </div>
        </TabsContent>
      </Tabs>

      {creating && <CreateTicketDialog isClient onClose={() => setCreating(false)} onCreated={load} />}
      {active && <TicketDrawer ticketId={active} onClose={() => setActive(null)} onChanged={load} />}
    </div>
  );
}
